/**
 * StatusBadge - Reusable order status badge component
 * 
 * Displays order status with appropriate styling and hover states.
 * Used across Dashboard, Order Details, and Processor Orders pages.
 */

import React from 'react';

// Status color mapping
const statusColors = {
  created: {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    border: 'border-gray-200',
    hoverBg: 'hover:bg-gray-200'
  },
  open: {
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    border: 'border-blue-200',
    hoverBg: 'hover:bg-blue-200'
  },
  in_progress: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-700',
    border: 'border-yellow-200',
    hoverBg: 'hover:bg-yellow-200'
  },
  pending: {
    bg: 'bg-orange-100',
    text: 'text-orange-700',
    border: 'border-orange-200',
    hoverBg: 'hover:bg-orange-200'
  },
  clarification: {
    bg: 'bg-purple-100',
    text: 'text-purple-700',
    border: 'border-purple-200',
    hoverBg: 'hover:bg-purple-200'
  },
  clarification_received: {
    bg: 'bg-indigo-100',
    text: 'text-indigo-700',
    border: 'border-indigo-200',
    hoverBg: 'hover:bg-indigo-200'
  },
  mail_away: {
    bg: 'bg-cyan-100',
    text: 'text-cyan-700',
    border: 'border-cyan-200',
    hoverBg: 'hover:bg-cyan-200'
  },
  mailaway: {
    bg: 'bg-cyan-100',
    text: 'text-cyan-700',
    border: 'border-cyan-200',
    hoverBg: 'hover:bg-cyan-200'
  },
  mail_away_received: {
    bg: 'bg-teal-100',
    text: 'text-teal-700',
    border: 'border-teal-200',
    hoverBg: 'hover:bg-teal-200'
  },
  complete: {
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-200',
    hoverBg: 'hover:bg-green-200'
  },
  cancelled: {
    bg: 'bg-red-100',
    text: 'text-red-700',
    border: 'border-red-200',
    hoverBg: 'hover:bg-red-200'
  }
};

// Status display names
const statusDisplayNames = {
  created: 'Created',
  open: 'Open',
  in_progress: 'In Progress',
  pending: 'Pending',
  clarification: 'Clarification',
  clarification_received: 'Clarification Received',
  mail_away: 'Mail-Away',
  mailaway: 'Mail-Away',
  mail_away_received: 'Mail-Away Received',
  complete: 'Complete',
  cancelled: 'Cancelled'
};

export const StatusBadge = ({ 
  status, 
  disableHover = false,
  size = 'default', // 'small', 'default', 'large'
  className = ''
}) => {
  const normalizedStatus = status?.toLowerCase()?.replace(/-/g, '_') || 'open';
  const colors = statusColors[normalizedStatus] || statusColors.open;
  const displayName = statusDisplayNames[normalizedStatus] || status;

  // Size classes
  const sizeClasses = {
    small: 'px-2 py-0.5 text-xs',
    default: 'px-3 py-1 text-sm',
    large: 'px-4 py-1.5 text-base'
  };

  return (
    <span
      className={`
        inline-flex items-center font-medium rounded-full border
        ${colors.bg} ${colors.text} ${colors.border}
        ${!disableHover ? colors.hoverBg : ''}
        ${!disableHover ? 'transition-colors cursor-default' : ''}
        ${sizeClasses[size]}
        ${className}
      `}
    >
      {displayName}
    </span>
  );
};

// Priority indicator component
export const PriorityIndicator = ({ priority, showLabel = false }) => {
  const priorityConfig = {
    normal: {
      color: 'text-gray-400',
      bgColor: 'bg-gray-100',
      label: 'Normal'
    },
    rush: {
      color: 'text-orange-500',
      bgColor: 'bg-orange-100',
      label: 'Rush'
    },
    urgent: {
      color: 'text-red-500',
      bgColor: 'bg-red-100',
      label: 'Urgent'
    }
  };

  const config = priorityConfig[priority?.toLowerCase()] || priorityConfig.normal;

  if (priority === 'normal' && !showLabel) {
    return null;
  }

  return (
    <span 
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}
      title={`Priority: ${config.label}`}
    >
      {priority === 'urgent' && (
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      )}
      {priority === 'rush' && (
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
        </svg>
      )}
      {showLabel && config.label}
    </span>
  );
};

export default StatusBadge;
